// Bulk data fetch for the content-health ("avisos") panel. One bounded set of
// queries — never N-per-resource — feeding the pure aggregator in
// utils/contentHealth. Owner-scoped (reuses fetchUserTours, which already
// applies the mock/normal demoScope). All reads, no writes.
//
// ponytail: query count scales with #tours, not their content. Fine for typical
// accounts (a handful each); revisit with a server-side RPC if a single owner
// ever holds hundreds of tours.
import type { VFloor, VRoom, VTour } from "@/types";
import { supabase } from "@cl/supabase";
import type { HealthHotspot, TourChildren } from "@u/contentHealth";
import { fetchUserTours } from "./tours";

export interface HealthData {
  tours: { tour: VTour; children: TourChildren }[];
}

export async function fetchHealthData(userId: string): Promise<HealthData> {
  const tours = await fetchUserTours(userId);
  return { tours: await buildTourPart(tours) };
}

async function buildTourPart(
  tours: VTour[],
): Promise<{ tour: VTour; children: TourChildren }[]> {
  const tourIds = tours.map((t) => t.id);
  if (tourIds.length === 0) return [];

  const [rooms, floors, scenes] = await Promise.all([
    supabase
      .from("v_rooms")
      .select("id,tour_id,name,translations,mts2,shape,edge_lengths")
      .in("tour_id", tourIds),
    supabase
      .from("v_floors")
      .select("id,tour_id,name,translations")
      .in("tour_id", tourIds),
    supabase.from("v_scenes").select("scene_id,tour_id").in("tour_id", tourIds),
  ]);

  const sceneToTour = new Map<string, string>();
  for (const s of (scenes.data ?? []) as { scene_id: string; tour_id: string }[])
    sceneToTour.set(s.scene_id, s.tour_id);
  const sceneIds = [...sceneToTour.keys()];

  const hotspots = sceneIds.length
    ? await supabase
        .from("v_hotspots")
        .select(
          "id,scene_id,type,target_scene_id,title,description,translations,img_url,audio_url,video_url,external_url",
        )
        .in("scene_id", sceneIds)
    : { data: [] as HealthHotspot[] };

  const sceneIdsByTour = groupBy(
    [...sceneToTour.entries()],
    ([, tourId]) => tourId,
  );

  const roomsByTour = groupBy(
    (rooms.data ?? []) as VRoom[],
    (r) => r.tour_id ?? "",
  );
  const floorsByTour = groupBy(
    (floors.data ?? []) as VFloor[],
    (f) => f.tour_id,
  );
  const hsByTour = groupBy(
    (hotspots.data ?? []) as HealthHotspot[],
    (h) => sceneToTour.get(h.scene_id) ?? "",
  );

  return tours.map((tour) => ({
    tour,
    children: {
      rooms: roomsByTour.get(tour.id) ?? [],
      floors: floorsByTour.get(tour.id) ?? [],
      hotspots: hsByTour.get(tour.id) ?? [],
      sceneIds: (sceneIdsByTour.get(tour.id) ?? []).map(([sid]) => sid),
    },
  }));
}

// ── Dismissals ────────────────────────────────────────────────────────
// Deliberate avisos the owner has hidden (see migration 20260720181902).

export async function fetchHealthDismissals(): Promise<string[]> {
  const { data, error } = await supabase
    .from("health_dismissals")
    .select("issue_key");
  if (error) throw error;
  return ((data ?? []) as { issue_key: string }[]).map((r) => r.issue_key);
}

export async function dismissHealthIssues(issueKeys: string[]): Promise<void> {
  if (issueKeys.length === 0) return;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const owner = session?.user?.id;
  if (!owner) throw new Error("Not authenticated");
  const { error } = await supabase
    .from("health_dismissals")
    .upsert(issueKeys.map((issue_key) => ({ owner_id: owner, issue_key })));
  if (error) throw error;
}

export async function restoreHealthIssues(issueKeys: string[]): Promise<void> {
  if (issueKeys.length === 0) return;
  const { error } = await supabase
    .from("health_dismissals")
    .delete()
    .in("issue_key", issueKeys);
  if (error) throw error;
}

function groupBy<T>(list: T[], key: (t: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of list) {
    const k = key(item);
    (map.get(k) ?? map.set(k, []).get(k)!).push(item);
  }
  return map;
}
